import {
    ChangeEvent,
    DetailedHTMLProps,
    forwardRef,
    ForwardRefExoticComponent,
    InputHTMLAttributes,
    ReactElement,
    RefAttributes,
    useContext,
    useEffect,
    useState,
} from 'react'

import { getRandomUuid } from '../../helpers/crypto'
import CheckboxGroupContext from './CheckboxGroupContext'

type AllCheckerCheckboxProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & RefAttributes<HTMLInputElement>;

const AllCheckerCheckbox: ForwardRefExoticComponent<AllCheckerCheckboxProps> = forwardRef((props, ref): ReactElement => {
    const {
        checked,
        disabled,
        onChange,
    } = props

    const [id] = useState(getRandomUuid())
    const checkboxGroup = useContext(CheckboxGroupContext)

    const [initialized, setInitialized] = useState(false)
    const [shouldTriggerCheckboxContextChange, setShouldTriggerCheckboxContextChange] = useState<boolean>(false)
    const [isChecked, setIsChecked] = useState<boolean | undefined>(checked !== undefined ? checked : checkboxGroup.defaultChecked)
    const [isDisabled, setIsDisabled] = useState(disabled !== undefined ? disabled : checkboxGroup.defaultDisabled)

    useEffect((): () => void => {
        checkboxGroup.assertIdDoesNotExist(id)
        return (): void => {
            checkboxGroup.allCheckerCheckboxes.delete(id)
        }
    }, [])

    useEffect(() => {
        setIsChecked(checked)
    }, [checked])

    useEffect((): void => {
        checkboxGroup.allCheckerCheckboxes.set(id, {
            isChecked,
            isDisabled,
            props,
            setIsChecked,
            setIsDisabled,
        })

        if (shouldTriggerCheckboxContextChange) {
            checkboxGroup.onAllCheckerCheckboxChange(id, initialized)
            setShouldTriggerCheckboxContextChange(false)
        }

        if (! initialized) {
            setInitialized(true)
        }
    }, [
        id, isChecked, isDisabled, setIsChecked, setIsDisabled, initialized,
        setShouldTriggerCheckboxContextChange, checkboxGroup, shouldTriggerCheckboxContextChange,
    ])

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        event.persist()

        if (! isDisabled) {
            setShouldTriggerCheckboxContextChange(true)
            setIsChecked(event.target.checked)
        }

        if (onChange !== undefined) {
            onChange(event)
        }
    }

    return (
        <input
            type="checkbox"
            {...props}
            ref={ref}
            onChange={handleChange}
            checked={isChecked !== undefined ? isChecked : false}
            disabled={isDisabled !== undefined ? isDisabled : false}
        />
    )
})

export default AllCheckerCheckbox
